import { useEffect, useRef } from 'react';
import { AsyncSectionNavigator, parseHtmlDocument, replaceSectionFromDocument } from '../../../ips-react-front-fw/navigation';
import { doneTopProgress, failTopProgress, startTopProgress, withTopProgressColor } from '../../../ips-react-front-fw/progress';
import { fetchHtmlPage } from './api';
import type { DashboardMountConfig } from './types';

const loadingClass = 'ips-react-dashboard-ajax-loading';
const overlayClass = 'ips-react-dashboard-ajax-overlay';
const positionedAttr = 'data-ips-react-overlay-positioned';

const addImageOverlay = (container: HTMLElement, imageUrl: string): void => {
  container.classList.add(loadingClass);

  if (window.getComputedStyle(container).position === 'static') {
    container.setAttribute(positionedAttr, '1');
    container.style.position = 'relative';
  }

  let overlay: HTMLElement | null = null;
  Array.from(container.children).forEach((child) => {
    if (!overlay && child.classList.contains(overlayClass)) {
      overlay = child as HTMLElement;
    }
  });

  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = overlayClass;
    container.appendChild(overlay);
  }

  if (imageUrl) {
    overlay.style.backgroundImage = `url("${imageUrl}")`;
  } else {
    overlay.style.backgroundImage = 'none';
  }

  overlay.style.display = 'block';
};

const removeImageOverlay = (container: HTMLElement): void => {
  container.classList.remove(loadingClass);

  const overlay = container.querySelector<HTMLElement>(`.${overlayClass}`);
  if (overlay) {
    overlay.style.display = 'none';
  }

  if (container.getAttribute(positionedAttr) === '1') {
    container.style.position = '';
    container.removeAttribute(positionedAttr);
  }
};

const toRelativeHref = (href: string): string => {
  try {
    const url = new URL(href, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    return href;
  }
};

const triggerCompatibilityEvents = (): void => {
  const maybeJquery = (window as any).jQuery;
  if (maybeJquery) {
    maybeJquery(document).trigger('ips-wcmap-myaccount-section-loaded');
    maybeJquery(document).trigger('ips_wcmap_myaccount_content_loaded');
    maybeJquery(document).trigger('ips_wcmap_myaccount_section_loaded');
  }

  document.dispatchEvent(new Event('ips-wcmap-myaccount-section-loaded'));
  document.dispatchEvent(new Event('ips_wcmap_myaccount_content_loaded'));
  document.dispatchEvent(new Event('ips_wcmap_myaccount_section_loaded'));
  document.dispatchEvent(new Event('ips:myaccount-content-updated'));
};

export const useDashboardAjaxNavigation = (config: DashboardMountConfig): void => {
  const navigatorRef = useRef<AsyncSectionNavigator | null>(null);

  if (!navigatorRef.current) {
    navigatorRef.current = new AsyncSectionNavigator();
  }

  useEffect(() => {
    if (!config.ajaxNavigation) {
      return;
    }

    const navigator = navigatorRef.current;
    if (!navigator) {
      return;
    }

    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest<HTMLAnchorElement>(config.selectors.navigationLinks);
      if (!anchor) {
        return;
      }

      const destination = anchor.getAttribute('href') || '';
      if (!destination) {
        return;
      }

      try {
        const parsed = new URL(destination, window.location.origin);
        if (parsed.origin !== window.location.origin) {
          return;
        }
      } catch (error) {
        return;
      }

      const container = document.querySelector<HTMLElement>(config.contentSelector);
      if (!container) {
        return;
      }

      event.preventDefault();

      const loaderMode = config.loader.mode;
      let clearProgressColor = () => {};
      let showTopProgress = false;

      if (loaderMode === 'option-3') {
        clearProgressColor = withTopProgressColor(config.loader.progressColor);
        showTopProgress = true;
        startTopProgress();
      } else {
        addImageOverlay(container, config.loader.imageUrl);
      }

      let hasError = false;

      const loadSection = async () => {
        try {
          const html = await navigator.run((signal) => fetchHtmlPage(destination, signal));
          const sourceDoc = parseHtmlDocument(html);
          const nextTitle = sourceDoc.querySelector('title')?.textContent?.trim() || '';
          const replaced = replaceSectionFromDocument(config.contentSelector, sourceDoc, { runScripts: true });

          if (!replaced) {
            window.location.href = destination;
            return;
          }

          const nextHref = toRelativeHref(destination);
          const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          if (nextHref !== currentHref) {
            window.history.replaceState({ url: nextHref }, '', nextHref);
          }

          if (nextTitle) {
            document.title = nextTitle;
          }

          if (config.ajaxNavigationScroll) {
            const top = replaced.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
              top,
              behavior: 'smooth',
            });
          }

          triggerCompatibilityEvents();
        } catch (error: any) {
          if (error?.name === 'AbortError') {
            return;
          }

          hasError = true;
          const status = Number(error?.status || 0);
          if (status === 404) {
            window.location.href = destination;
            return;
          }

          window.location.href = destination;
        } finally {
          if (showTopProgress) {
            if (hasError) {
              failTopProgress();
            } else {
              doneTopProgress();
            }
            clearProgressColor();
          } else {
            const refreshedContainer = document.querySelector<HTMLElement>(config.contentSelector) || container;
            removeImageOverlay(refreshedContainer);
          }
        }
      };

      void loadSection();
    };

    document.addEventListener('click', onClick, true);
    return () => {
      navigator.abort();
      document.removeEventListener('click', onClick, true);
    };
  }, [
    config.ajaxNavigation,
    config.ajaxNavigationScroll,
    config.contentSelector,
    config.loader.imageUrl,
    config.loader.mode,
    config.loader.progressColor,
    config.selectors.navigationLinks,
  ]);
};
